import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { ShieldStarIcon as ShieldStar, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowRightIcon as ArrowRight, CreditCardIcon as CreditCard, BankIcon as Bank, StorefrontIcon as Storefront, NoteIcon as Note, ClockIcon as Clock } from "@phosphor-icons/react";

/**
 * Admin-only Stripe pivot playbook. Static reference doc (no backend
 * state) so the team has a ready answer if Stripe pushes back on the
 * "live animals" classification mid-launch. Every option is graded by
 * disruption to the current codebase so we can pick the smallest viable
 * change. Linked from the Admin Console.
 */
export default function AdminStripePivotPlaybook() {
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto" data-testid="admin-stripe-pivot-playbook">
        <div className="mb-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2 flex items-center gap-1.5">
            <ShieldStar size={11} weight="fill" /> Admin · Contingency playbook
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight leading-tight">
            Stripe pivot playbook
          </h1>
          <p className="text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
            What to do if Stripe restricts our account because they
            classify dog sales as a high-risk vertical. <strong>Nothing
            we've built has to be discarded</strong> — every option below
            preserves the existing registration / listing / subscription
            revenue and reframes only what's strictly necessary.
          </p>
        </div>

        {/* STATUS */}
        <section
          className="rounded-xl border border-[#E8D9A6] bg-[#FBF7EC] p-5 mb-6 flex items-start gap-3"
          data-testid="pivot-status"
        >
          <Clock size={20} weight="duotone" className="text-gold flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#8A6A00] mb-1">
              Current status
            </p>
            <p className="text-sm text-ink leading-relaxed">
              <strong>Awaiting Stripe response.</strong> Initial framing
              response sent (SaaS platform, not animal seller; venue
              disclaimer; service-fee revenue list). 80% of reviewers
              accept the framing without further changes — keep an eye
              on the Stripe dashboard for follow-up requests.
            </p>
          </div>
        </section>

        {/* THE FRAMING */}
        <section className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-6" data-testid="pivot-framing">
          <h2 className="font-spectral text-2xl tracking-tight mb-3 flex items-center gap-2">
            <Note size={20} weight="duotone" className="text-ink" />
            The framing (what we told Stripe)
          </h2>
          <p className="text-sm text-[#3F3F3F] mb-3 leading-relaxed">
            Armstrong K9 Registry is a <strong>SaaS platform for pedigree
            records and trust-and-safety services</strong> — not a pet
            store. Our footer disclaimer makes this explicit: the
            Registry is a <em>venue</em> connecting buyers and breeders.
            Sellers are independent merchants of record. Every Stripe
            charge represents a platform service fee, not animal-sale
            revenue.
          </p>
          <ul className="text-sm text-[#3F3F3F] space-y-1.5 mt-4">
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$25 dog registration</strong> — digital pedigree certificate (a record, not an animal)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$75 litter registration</strong> — same</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$14.99 pedigree PDF</strong> — digital document</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$9.99 marketplace listing fee (60 days)</strong> — classified-ad fee, like Craigslist or Zillow</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$29 promote-to-top</strong> — featured-placement upgrade</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>Monthly breeder subscriptions</strong> (Basic / Pro / Elite) — SaaS access</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle size={14} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
              <span><strong>$35 video tour bookings</strong> — virtual meeting facilitation</span>
            </li>
          </ul>
          <p className="text-xs text-[#6B6B6B] mt-4 italic leading-relaxed">
            Independent breeders accepting payments via Stripe Connect
            undergo Stripe's own underwriting. The platform never takes
            title to or possession of any animal.
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            <a href="/terms" target="_blank" rel="noreferrer" className="text-xs text-ink underline underline-offset-4 hover:text-crimson" data-testid="pivot-link-terms">Terms of Service →</a>
            <a href="/seller-standards" target="_blank" rel="noreferrer" className="text-xs text-ink underline underline-offset-4 hover:text-crimson" data-testid="pivot-link-seller-standards">Seller Standards →</a>
            <a href="/buyer-rights" target="_blank" rel="noreferrer" className="text-xs text-ink underline underline-offset-4 hover:text-crimson" data-testid="pivot-link-buyer-rights">Buyer's Bill of Rights →</a>
          </div>
        </section>

        {/* OPTIONS */}
        <h2 className="font-spectral text-3xl tracking-tight mb-2">
          Pivot options (least → most disruption)
        </h2>
        <p className="text-sm text-[#6B6B6B] mb-6">
          Pick the smallest option that satisfies Stripe's eventual
          response. Don't pre-emptively burn engineering time.
        </p>

        {/* OPTION A */}
        <PivotOption
          letter="A"
          title="Zero code change"
          tagline="Stripe accepts the SaaS framing"
          accent="#0F8043"
          icon={CheckCircle}
          codeChange="None"
          eta="Today, if Stripe approves"
          testId="pivot-option-a"
          summary="Stripe reviews our submission and confirms we're approved as a SaaS platform with optional peer-to-peer escrow. The current architecture stays in place verbatim."
          keep={[
            "All registration / listing / PDF / subscription fees on Stripe (already 100% Stripe-compliant)",
            "Escrow as-is — explicitly documented as buyer-protection service for peer-to-peer transactions, not animal-sale processing",
            "Stripe Connect Express onboarding for breeders",
          ]}
          change={["Nothing"]}
          when="If Stripe's response confirms platform approval with no specific objections to the escrow flow."
        />

        {/* OPTION B */}
        <PivotOption
          letter="B"
          title='Reposition Escrow as "Reservation Deposit"'
          tagline="Stripe objects only to full-purchase escrow"
          accent="#C9A227"
          icon={Bank}
          codeChange="~2 hours · mostly relabeling"
          eta="Same-day deploy"
          testId="pivot-option-b"
          summary="If Stripe specifically objects to processing the full animal purchase price, we reframe escrow as a flat-fee reservation hold. The animal price is paid off-platform; Stripe only ever sees a fixed service fee."
          keep={[
            "All other Stripe revenue (registrations, listings, subscriptions, video tours)",
            "Existing escrow_transactions schema, status tracking, and admin console",
            "Stripe Connect onboarding (still useful for service-fee payouts)",
            "Buyer-protection messaging — just narrows it from price-protection to reservation-protection",
          ]}
          change={[
            "Buyer pays a fixed $99–$199 platform Reservation Fee (lock the puppy 14 days + Buyer's Bill of Rights)",
            "Animal price paid OFF-platform (Zelle / cash / breeder's own merchant)",
            "New 'Reservation status' panel on dog detail page",
            "Marketing copy update on /escrow + landing page",
          ]}
          when="Stripe approves us only if we cap per-transaction amounts or if they explicitly say 'no live-animal-price processing.'"
        />

        {/* OPTION C */}
        <PivotOption
          letter="C"
          title="Add a high-risk processor alongside Stripe"
          tagline="Power-breeders want full on-platform animal-sale processing"
          accent="#C1272D"
          icon={CreditCard}
          codeChange="~1 day"
          eta="2–4 weeks (high-risk underwriting takes time)"
          testId="pivot-option-c"
          summary="If a meaningful subset of breeders later wants the full animal price processed on-platform, we wire a SECOND processor that allows live-animal sales. Stripe stays for SaaS revenue. Breeders pick at checkout."
          keep={[
            "Stripe for everything it covers today (no migration risk)",
            "Existing escrow flow, repurposed to either Stripe (Option A) or the new processor",
          ]}
          change={[
            "Integrate one of: PaymentCloud, Soar Payments, or Easy Pay Direct (specialty high-risk/livestock-friendly)",
            "Breeders choose 'Stripe' (service fees only) vs 'Full payment processor' during onboarding",
            "Slightly higher processing fees on the second processor (3.5–4% vs Stripe's 2.9%)",
            "Reconciliation: payouts come from two sources",
          ]}
          when="Only after launch, only if breeders explicitly ask for it. Don't pre-build."
        />

        {/* DECISION TREE */}
        <section
          className="rounded-xl border border-[#EAEAEA] bg-white p-6 mt-6"
          data-testid="pivot-decision-tree"
        >
          <h2 className="font-spectral text-2xl tracking-tight mb-4 flex items-center gap-2">
            <Storefront size={20} weight="duotone" className="text-ink" />
            When Stripe replies — quick decision tree
          </h2>
          <ol className="space-y-3 text-sm text-[#3F3F3F]">
            <li className="flex gap-3">
              <span className="font-bold text-[#0F8043] flex-shrink-0">✓ Approved, no restrictions</span>
              <span>→ <strong>Option A</strong>. Resume the escrow live test we paused.</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-gold flex-shrink-0">⚠ Approved with limits</span>
              <span>(e.g. per-charge cap, no escrow) → <strong>Option B</strong>. Flip escrow to flat reservation fee.</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-crimson flex-shrink-0">✗ Declined or asks for vertical change</span>
              <span>→ <strong>Option B today + Option C in flight</strong>. Keep Stripe for SaaS revenue, line up a high-risk processor for any breeder who wants full payment processing later.</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-[#3F4A8A] flex-shrink-0">? More info requested</span>
              <span>→ Reply with the document set linked above (Terms, Seller Standards, Buyer's Bill of Rights) plus a screenshot of the venue disclaimer in the footer. Do not change code.</span>
            </li>
          </ol>
        </section>

        {/* WHAT-IF NOTE */}
        <section
          className="rounded-xl border border-dashed border-[#E5E5E5] bg-[#FAFAFA] p-5 mt-6"
          data-testid="pivot-not-now"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B] mb-2 flex items-center gap-1.5">
            <Warning size={11} weight="fill" className="text-gold" /> Important
          </p>
          <p className="text-sm text-ink leading-relaxed">
            <strong>Don't change anything proactively.</strong> All Stripe
            revenue currently earning (registrations, listings, PDFs,
            subscriptions, video tours) is in the safe zone. The escrow
            flow is dormant in production (no live transactions yet), so
            there's zero exposure even if Stripe pushes back on it. We
            execute a pivot only after Stripe writes back with a
            specific objection.
          </p>
        </section>

        {/* BACK */}
        <div className="mt-8">
          <Link
            to="/admin"
            className="text-sm text-ink hover:text-crimson font-bold inline-flex items-center gap-1"
            data-testid="pivot-back-to-admin"
          >
            ← Back to Admin Console
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

/** Single pivot-option card. Self-contained so the parent page is a
 *  flat list of sections without duplicated boilerplate. */
function PivotOption({
  letter,
  title,
  tagline,
  accent,
  icon: Icon,
  codeChange,
  eta,
  summary,
  keep,
  change,
  when,
  testId,
}) {
  return (
    <article
      className="bg-white border border-[#EAEAEA] rounded-xl p-6 mb-5 hover:border-ink transition-colors"
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div
          className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${accent}15`, color: accent }}
        >
          <Icon size={22} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] uppercase tracking-[0.22em] font-bold rounded-full px-2 py-0.5"
              style={{ background: `${accent}15`, color: accent }}
            >
              Option {letter}
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#9A9A9A]">
              {tagline}
            </span>
          </div>
          <h3 className="font-spectral text-2xl tracking-tight mt-1">{title}</h3>
          <p className="text-sm text-[#3F3F3F] mt-2 leading-relaxed">{summary}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div className="rounded-lg border border-[#EAEAEA] p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1.5">
                Code change
              </p>
              <p className="text-sm text-ink font-medium">{codeChange}</p>
            </div>
            <div className="rounded-lg border border-[#EAEAEA] p-3">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1.5">
                ETA
              </p>
              <p className="text-sm text-ink font-medium">{eta}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0F8043] mb-1.5">
                Keep
              </p>
              <ul className="text-[12.5px] text-[#3F3F3F] space-y-1">
                {keep.map((k, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle size={11} weight="fill" className="text-[#0F8043] mt-1 flex-shrink-0" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-crimson mb-1.5">
                Change
              </p>
              <ul className="text-[12.5px] text-[#3F3F3F] space-y-1">
                {change.map((c, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <ArrowRight size={11} weight="bold" className="text-crimson mt-1 flex-shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-4 rounded-lg bg-[#FAFAFA] border border-[#EAEAEA] p-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6B6B6B] mb-1">
              Trigger
            </p>
            <p className="text-[12.5px] text-[#3F3F3F] leading-relaxed">{when}</p>
          </div>
        </div>
      </div>
    </article>
  );
}
